/**
 * Meta App Review reviewer sign-in (api/app/reviewer.py, owner 19 ต.ค. 69 — Meta's reviewers test the chat without the POS):
 *   GET  /auth/reviewer/status                {open}  public: the /login page (components/ReviewerSignIn.vue)
 *   POST /auth/reviewer {username, password}  204 + the session cookie · 401 wrong · 403 closed / origin · 429 locked
 *   GET/PUT /api/v1/config/reviewer {enabled} · POST /api/v1/config/reviewer/password
 *                                             owner only: ตั้งค่า › ช่องทางที่เชื่อม (config/ReviewerLoginSetting.vue)
 * A new password comes back once, in the answer that made it (`password`); the server never sends it again.
 * Self-contained like ./metaBusiness.ts: mock mode keeps the switch in memory (VITE_USE_MOCK, screens tests).
 */
import { ApiError } from './client'
import { reportUnauthorized } from './http'
import { useMock } from './index'

export interface ReviewerSettings {
  enabled: boolean
  /** fixed: meta-reviewer */
  username: string
  /** the sign-in page to give Meta: `${origin}${path}` */
  path: string
  has_password: boolean
  password_set_at: string | null
  /** switched on at (null while off) */
  enabled_at: string | null
  disabled_at: string | null
  last_login_at: string | null
  login_count: number
  /** reviewer sessions that still work */
  live_sessions: number
  /** only in the answer that made a new password */
  password?: string
}

export type SignInResult = 'ok' | 'wrong' | 'locked' | 'closed' | 'origin' | 'invalid' | 'error'

export interface ReviewerPublicApi {
  /** Is reviewer sign-in switched on? Throws when the server cannot be reached. */
  status(): Promise<boolean>
  signIn(username: string, password: string): Promise<SignInResult>
}

export interface ReviewerSettingsApi {
  get(): Promise<ReviewerSettings>
  setEnabled(enabled: boolean): Promise<ReviewerSettings>
  newPassword(): Promise<ReviewerSettings>
}

const base = (): string => (import.meta.env.VITE_API_BASE ?? '').trim().replace(/\/+$/, '')
const CODES = new Set<SignInResult>(['wrong', 'locked', 'closed', 'origin', 'invalid'])

async function send(method: 'GET' | 'POST' | 'PUT', path: string, body?: unknown): Promise<Response> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), 15000)
  try {
    return await fetch(new URL(`${base()}${path}`, window.location.origin).toString(), {
      method,
      headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์')
    throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
  } finally {
    clearTimeout(timer)
  }
}

async function settingsCall(method: 'GET' | 'POST' | 'PUT', path: string, body?: unknown): Promise<ReviewerSettings> {
  const res = await send(method, `/api/v1/config/reviewer${path}`, body)
  if (res.status === 401) reportUnauthorized()
  if (!res.ok) {
    let detail = res.statusText
    try {
      const j = (await res.json()) as { detail?: unknown }
      if (typeof j.detail === 'string') detail = j.detail
    } catch {
      /* body not JSON */
    }
    throw new ApiError(res.status, detail || `HTTP ${res.status}`)
  }
  return (await res.json()) as ReviewerSettings
}

const httpPublic: ReviewerPublicApi = {
  async status() {
    const res = await send('GET', '/auth/reviewer/status')
    if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`)
    return ((await res.json()) as { open?: unknown }).open === true
  },
  async signIn(username, password) {
    let res: Response
    try {
      res = await send('POST', '/auth/reviewer', { username, password })
    } catch {
      return 'error'
    }
    if (res.status === 204 || res.ok) return 'ok'
    try {
      const code = ((await res.json()) as { code?: unknown }).code
      if (typeof code === 'string' && CODES.has(code as SignInResult)) return code as SignInResult
    } catch {
      /* body not JSON */
    }
    return 'error'
  },
}

const httpSettings: ReviewerSettingsApi = {
  get: () => settingsCall('GET', ''),
  setEnabled: (enabled) => settingsCall('PUT', '', { enabled }),
  newPassword: () => settingsCall('POST', '/password'),
}

// ------------------------------------------------------------------ mock (VITE_USE_MOCK, screens tests)

const ALPHABET = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'
const mockState: ReviewerSettings = {
  enabled: false,
  username: 'meta-reviewer',
  path: '/login',
  has_password: false,
  password_set_at: null,
  enabled_at: null,
  disabled_at: null,
  last_login_at: null,
  login_count: 0,
  live_sessions: 0,
}
let mockSecret: string | null = null

function mockPassword(): string {
  const out = new Uint32Array(20)
  crypto.getRandomValues(out)
  return Array.from(out, (n) => ALPHABET[n % ALPHABET.length]).join('')
}

function mockView(password?: string): ReviewerSettings {
  return password ? { ...mockState, password } : { ...mockState }
}

function mockRole(): string | null {
  try {
    return localStorage.getItem('cf-chat.mock-role')
  } catch {
    return null
  }
}

function mockOwner(): void {
  if (mockRole() !== 'owner') throw new ApiError(403, 'เฉพาะเจ้าของ')
}

const mockSettings: ReviewerSettingsApi = {
  async get() {
    mockOwner()
    return mockView()
  },
  async setEnabled(enabled) {
    mockOwner()
    const now = new Date().toISOString()
    if (enabled) {
      let made: string | undefined
      if (!mockSecret) {
        made = mockSecret = mockPassword()
        Object.assign(mockState, { has_password: true, password_set_at: now })
      }
      if (!mockState.enabled) Object.assign(mockState, { enabled: true, enabled_at: now })
      return mockView(made)
    }
    Object.assign(mockState, {
      disabled_at: mockState.enabled ? now : mockState.disabled_at,
      enabled: false,
      enabled_at: null,
      has_password: false,
      password_set_at: null,
      live_sessions: 0,
    })
    mockSecret = null
    return mockView()
  },
  async newPassword() {
    mockOwner()
    const made = (mockSecret = mockPassword())
    Object.assign(mockState, { has_password: true, password_set_at: new Date().toISOString(), live_sessions: 0 })
    return mockView(made)
  },
}

const mockPublic: ReviewerPublicApi = {
  async status() {
    return mockState.enabled && !!mockSecret
  },
  async signIn(username, password) {
    if (!mockState.enabled || !mockSecret) return 'closed'
    if (username.trim().toLowerCase() !== mockState.username || password !== mockSecret) return 'wrong'
    Object.assign(mockState, { last_login_at: new Date().toISOString(), login_count: mockState.login_count + 1 })
    return 'ok'
  },
}

let publicImpl: ReviewerPublicApi | null = null
let settingsImpl: ReviewerSettingsApi | null = null

/** Tests inject their own. */
export function setReviewerApis(pub: ReviewerPublicApi | null, settings: ReviewerSettingsApi | null = null): void {
  publicImpl = pub
  settingsImpl = settings
}

export function reviewerPublicApi(): ReviewerPublicApi {
  return publicImpl ?? (useMock ? mockPublic : httpPublic)
}

export function reviewerSettingsApi(): ReviewerSettingsApi {
  return settingsImpl ?? (useMock ? mockSettings : httpSettings)
}

/** The address to give Meta (App Review › "instructions for the reviewer"). */
export function reviewerUrl(s: Pick<ReviewerSettings, 'path'>, origin: string = window.location.origin): string {
  return `${origin}${s.path}`
}
